import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import { Search, CheckCircle, Block, OpenInNew } from '@mui/icons-material';
import { checkBorrowerEligibility } from '../../../services/request/Borrowers';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PageHeader } from '../../../shared-components/ui';

interface EligibilityResult {
  eligible?: boolean;
  blacklistedMatch?: boolean;
  matchType?: string;
  message?: string;
  matchedBorrower?: {
    id?: string;
    name?: string;
    email?: string;
    contactNumber?: string;
    creditStatus?: string;
    blacklistReason?: string;
  };
  checksPerformed?: string[];
}

const EligibilityCheck: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EligibilityResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const eTrim = email.trim();
    const mTrim = mobile.trim();
    if (!eTrim && !mTrim) {
      toast.error('Enter an email and/or mobile number');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await checkBorrowerEligibility({
        email: eTrim || undefined,
        mobile: mTrim || undefined,
      });
      const body = res as {
        data?: EligibilityResult;
        statusCode?: number;
        message?: string;
      };
      if (body?.statusCode === 200 && body.data) {
        setResult(body.data);
        if (body.data.eligible) {
          toast.success('Eligible — no blacklist match');
        } else {
          toast.warning('Not eligible — blacklist match');
        }
      } else if (body?.statusCode === 400) {
        toast.error(body.message || 'Invalid request');
      } else {
        toast.error('Unexpected response');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data ||
        err?.message ||
        'Check failed';
      toast.error(typeof msg === 'string' ? msg : 'Check failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setEmail('');
    setMobile('');
    setResult(null);
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <ToastContainer position="top-right" autoClose={3500} />
      <PageHeader
        eyebrow="Lending · Ops"
        title="Eligibility check"
        subtitle="Validates against the blacklisted customers list (email and/or mobile)."
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        Enter at least one field. Email is compared case-insensitively. Mobile matches primary contact or WhatsApp
        (spaces and +91-style prefixes are ignored).
      </Alert>

      <Card variant="outlined">
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                fullWidth
                autoComplete="off"
                placeholder="applicant@example.com"
              />
              <TextField
                label="Mobile number"
                value={mobile}
                onChange={(ev) => setMobile(ev.target.value)}
                fullWidth
                autoComplete="off"
                placeholder="+91 98765 43210"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Search />}
                  disabled={loading}
                >
                  Run check
                </Button>
                <Button type="button" variant="outlined" onClick={handleClear} disabled={loading}>
                  Clear
                </Button>
              </Stack>
            </Stack>
          </form>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </CardContent>
      </Card>

      {result && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              {result.eligible ? (
                <CheckCircle color="success" fontSize="large" />
              ) : (
                <Block color="error" fontSize="large" />
              )}
              <Typography variant="h6">
                {result.eligible ? 'Eligible' : 'Not eligible'}
              </Typography>
              <Chip
                size="small"
                label={result.blacklistedMatch ? 'Blacklist hit' : 'No blacklist hit'}
                color={result.blacklistedMatch ? 'error' : 'success'}
                variant="outlined"
              />
            </Stack>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {result.message}
            </Typography>
            {result.matchType && result.matchType !== 'NONE' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Matched on: <strong>{result.matchType}</strong>
              </Typography>
            )}
            {result.checksPerformed && result.checksPerformed.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Checks performed
                </Typography>
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  {result.checksPerformed.map((c) => (
                    <Chip key={c} label={c} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}
            {result.matchedBorrower?.id && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Matched borrower (admin)
                </Typography>
                <Typography variant="body2">
                  {result.matchedBorrower.name} · {result.matchedBorrower.creditStatus}
                </Typography>
                {result.matchedBorrower.blacklistReason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Reason: {result.matchedBorrower.blacklistReason}
                  </Typography>
                )}
                <Button
                  size="small"
                  endIcon={<OpenInNew />}
                  sx={{ mt: 1 }}
                  onClick={() => navigate(`/admin/customers/${result.matchedBorrower!.id}/debits`)}
                >
                  Open customer debits
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EligibilityCheck;
